/**
 * localStorage/sessionStorage erişimi bazı tarayıcılarda throw eder:
 * Safari private mode, çerezleri kapatan Android WebView'ları, gömülü in-app
 * tarayıcılar ve kota dolduğunda tüm tarayıcılar. Modül seviyesinde çağrıldığında
 * bu hata tüm uygulamayı beyaz ekrana düşürür, bu yüzden her erişim sarmalanmıştır.
 */

const getStore = (session) => {
  try {
    const store = session ? window.sessionStorage : window.localStorage;
    // Erişimin gerçekten çalıştığını doğrula (bazı tarayıcılar sadece okumada throw eder).
    const probe = "__storage_probe__";
    store.setItem(probe, probe);
    store.removeItem(probe);
    return store;
  } catch {
    return null;
  }
};

const memoryStore = new Map();

const read = (key, session) => {
  const store = getStore(session);
  if (!store) {
    return memoryStore.has(key) ? memoryStore.get(key) : null;
  }
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
};

const write = (key, value, session) => {
  const store = getStore(session);
  if (!store) {
    memoryStore.set(key, value);
    return false;
  }
  try {
    store.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const drop = (key, session) => {
  memoryStore.delete(key);
  const store = getStore(session);
  if (!store) return false;
  try {
    store.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

export const getItem = (key) => read(key, false);
export const setItem = (key, value) => write(key, value, false);
export const removeItem = (key) => drop(key, false);

export const getSessionItem = (key) => read(key, true);
export const setSessionItem = (key, value) => write(key, value, true);
export const removeSessionItem = (key) => drop(key, true);

/**
 * JSON.parse hatalarını yutar ve bozuk kaydı temizler.
 * Örn. JSON.stringify(undefined) -> "undefined" yazılmışsa JSON.parse throw eder
 * ve modül seviyesindeki her okuma kalıcı beyaz ekrana yol açar.
 */
export const getJson = (key, fallback = null) => {
  const raw = getItem(key);
  if (raw === null || raw === undefined || raw === "" || raw === "undefined") {
    return fallback;
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed === null || parsed === undefined ? fallback : parsed;
  } catch {
    removeItem(key);
    return fallback;
  }
};

export const setJson = (key, value) => {
  if (value === undefined) {
    return removeItem(key);
  }
  try {
    return setItem(key, JSON.stringify(value));
  } catch {
    return false;
  }
};

/** Oturum/tercih verisini sıfırla (beyaz ekran kurtarma akışı için). */
export const clearAppStorage = () => {
  const store = getStore(false);
  memoryStore.clear();
  if (!store) return false;
  try {
    store.clear();
    return true;
  } catch {
    return false;
  }
};

const safeStorage = {
  getItem,
  setItem,
  removeItem,
  getSessionItem,
  setSessionItem,
  removeSessionItem,
  getJson,
  setJson,
  clearAppStorage,
};

export default safeStorage;
